import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookMarked, Search, Plus, Book, Video, FileText, Download, Upload,
  Filter, BookOpen, Clock
} from 'lucide-react';

export const LibraryPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [myBorrows, setMyBorrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchItems();
    fetchMyBorrows();
  }, [typeFilter]);

  const fetchItems = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      
      const response = await axios.get(`${API_URL}/library/items?${params}`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching library items:', error);
      // Demo data
      setItems([
        { id: '1', title: 'Introduction à l\'Informatique', author: 'Jean Mukendi', type: 'book', category: 'Informatique', available_copies: 3, total_copies: 5, is_digital: false },
        { id: '2', title: 'Algèbre Linéaire', author: 'Marie Kabongo', type: 'ebook', category: 'Mathématiques', available_copies: 999, total_copies: 999, is_digital: true },
        { id: '3', title: 'Physique Quantique', author: 'Paul Tshisekedi', type: 'book', category: 'Physique', available_copies: 0, total_copies: 2, is_digital: false },
        { id: '4', title: 'Cours de Programmation Python', author: 'Sarah Kasongo', type: 'video', category: 'Informatique', available_copies: 999, total_copies: 999, is_digital: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBorrows = async () => {
    // In production, fetch from API
    setMyBorrows([
      { id: '1', item: { title: 'Calcul Différentiel' }, borrowed_at: '2026-01-01', due_date: '2026-01-15', status: 'borrowed' }
    ]);
  };

  const borrowItem = async (itemId) => {
    try {
      await axios.post(`${API_URL}/library/borrow`, { item_id: itemId });
      toast.success(language === 'fr' ? 'Livre emprunté' : 'Book borrowed');
      fetchItems();
      fetchMyBorrows();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const filteredItems = items.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const typeIcons = {
    book: Book,
    ebook: FileText,
    video: Video,
    document: FileText
  };

  const isAdmin = ['super_admin', 'rector', 'admin_staff'].includes(user?.role);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">{t('library')}</h1>
          <p className="text-gray-500 mt-1">
            {language === 'fr' 
              ? `${items.length} ressources disponibles`
              : `${items.length} resources available`}
          </p>
        </div>
        {isAdmin && <AddLibraryItemDialog onAdd={fetchItems} />}
      </div>

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog">
            {language === 'fr' ? 'Catalogue' : 'Catalog'}
          </TabsTrigger>
          <TabsTrigger value="borrowed">
            {language === 'fr' ? 'Mes emprunts' : 'My Borrows'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={language === 'fr' ? 'Rechercher un livre...' : 'Search books...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="library-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                <SelectItem value="book">{language === 'fr' ? 'Livres' : 'Books'}</SelectItem>
                <SelectItem value="ebook">E-books</SelectItem>
                <SelectItem value="video">{language === 'fr' ? 'Vidéos' : 'Videos'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Items Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-32 bg-gray-200 rounded mb-3" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-1/2 bg-gray-200 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredItems.map(item => {
                const Icon = typeIcons[item.type] || Book;
                return (
                  <Card key={item.id} className="card-hover" data-testid={`library-item-${item.id}`}>
                    <CardContent className="p-4">
                      <div className={`h-32 rounded-lg flex items-center justify-center mb-3 ${
                        item.type === 'book' ? 'bg-blue-50' :
                        item.type === 'ebook' ? 'bg-green-50' :
                        item.type === 'video' ? 'bg-purple-50' : 'bg-gray-50'
                      }`}>
                        <Icon className={`w-12 h-12 ${
                          item.type === 'book' ? 'text-blue-500' :
                          item.type === 'ebook' ? 'text-green-500' :
                          item.type === 'video' ? 'text-purple-500' : 'text-gray-500'
                        }`} />
                      </div>
                      <Badge variant="secondary" className="mb-2 text-xs">
                        {item.category || item.type}
                      </Badge>
                      <h3 className="font-medium text-sm mb-1 line-clamp-2">{item.title}</h3>
                      <p className="text-xs text-gray-500 mb-3">{item.author}</p>
                      
                      <div className="flex items-center justify-between">
                        {item.is_digital ? (
                          <Badge className="badge-success text-xs">
                            {language === 'fr' ? 'Numérique' : 'Digital'}
                          </Badge>
                        ) : (
                          <Badge variant={item.available_copies > 0 ? 'default' : 'secondary'} className="text-xs">
                            {item.available_copies}/{item.total_copies} {language === 'fr' ? 'dispo' : 'avail'}
                          </Badge>
                        )}
                        
                        {item.is_digital ? (
                          <Button size="sm" variant="outline" className="gap-1">
                            <Download className="w-3 h-3" />
                            {language === 'fr' ? 'Ouvrir' : 'Open'}
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            disabled={item.available_copies === 0}
                            onClick={() => borrowItem(item.id)}
                          >
                            {language === 'fr' ? 'Emprunter' : 'Borrow'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="borrowed" className="mt-4">
          <Card>
            <CardContent className="p-6">
              {myBorrows.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{language === 'fr' ? 'Aucun emprunt en cours' : 'No active borrows'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {myBorrows.map(borrow => (
                    <div key={borrow.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Book className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium">{borrow.item?.title}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Clock className="w-3 h-3" />
                            {language === 'fr' ? 'À retourner le' : 'Due'}: {new Date(borrow.due_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <Button variant="outline">
                        {language === 'fr' ? 'Retourner' : 'Return'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AddLibraryItemDialog = ({ onAdd }) => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    type: 'book',
    category: '',
    description: '',
    total_copies: 1,
    is_digital: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/library/items`, formData);
      toast.success(language === 'fr' ? 'Ressource ajoutée' : 'Resource added');
      setOpen(false);
      setFormData({ title: '', author: '', type: 'book', category: '', description: '', total_copies: 1, is_digital: false });
      onAdd?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-library-item">
          <Plus className="w-4 h-4" />
          {language === 'fr' ? 'Ajouter une ressource' : 'Add Resource'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{language === 'fr' ? 'Nouvelle ressource' : 'New Resource'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('title')}</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'fr' ? 'Auteur' : 'Author'}</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData(prev => ({ ...prev, type: v, is_digital: v !== 'book' }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="book">{language === 'fr' ? 'Livre' : 'Book'}</SelectItem>
                  <SelectItem value="ebook">E-book</SelectItem>
                  <SelectItem value="video">{language === 'fr' ? 'Vidéo' : 'Video'}</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'fr' ? 'Catégorie' : 'Category'}</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Informatique"
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Exemplaires' : 'Copies'}</Label>
              <Input
                type="number"
                value={formData.total_copies}
                onChange={(e) => setFormData(prev => ({ ...prev, total_copies: parseInt(e.target.value) }))}
                min={1}
              />
            </div>
          </div>
          <div>
            <Label>{t('description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LibraryPage;
